import { css } from 'aphrodite';
import * as React from 'react';
import { ISupportGitHubIssueLink } from '../../../models/Support/ISupportGitHub';
import {
	useSupportGitHubQuery,
	useSyncGitHubIssueMutation,
	useUnlinkGitHubIssueLinkMutation,
} from '../../../queries/Support/useSupportGitHub';
import { getApiErrorMessage, isNotFoundError } from '../../../utils/Support/supportHelpers';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Button } from '../../../web/components-v2/Button';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';
import { GitHubUpdateIssueModal } from './GitHubIssueModals';
import { GitHubGlyph } from './inboxIcons';
import { styles } from './styles';
import { formatDateTime } from './supportTicketPresentation';

interface GitHubPanelProps {
	ticketId: string;
	refetchInterval?: number | false;
	onCreateIssue?: () => void;
	onLinkIssue?: () => void;
}

export const GitHubPanel: React.FC<GitHubPanelProps> = ({ ticketId, refetchInterval, onCreateIssue, onLinkIssue }) => {
	const query = useSupportGitHubQuery(ticketId, { refetchInterval });
	const issues = query.data?.issues ?? [];
	const legacyIssues = query.data?.legacyIssues ?? [];
	const hasAnyLinks = issues.length > 0 || legacyIssues.length > 0;

	// A 404 means the GitHub endpoints don't exist in this environment (e.g. the test API
	// predates the integration). Render a quiet not-available state — the actions would fail too.
	const integrationMissing = query.isError && isNotFoundError(query.error);

	// The status pill reflects real query state rather than a connection probe we don't have:
	// an error surfaces as Unavailable, an in-flight fetch as Syncing, and an idle success as Connected.
	const statusLabel = integrationMissing
		? 'Not available'
		: query.isError
			? 'Unavailable'
			: query.isFetching
				? 'Syncing…'
				: 'Connected';
	const statusStyle = query.isError
		? integrationMissing
			? styles.githubStatusSyncing
			: styles.githubStatusError
		: query.isFetching
			? styles.githubStatusSyncing
			: styles.githubStatusConnected;

	if (integrationMissing) {
		return (
			<section className={css(styles.githubPanel)} aria-label='GitHub issue integration'>
				<div className={css(styles.githubPanelHeader)}>
					<span className={css(styles.githubPanelIcon)}>
						<GitHubGlyph size={22} />
					</span>
					<div className={css(styles.githubPanelHeading)}>
						<div className={css(styles.githubPanelTitle)}>GitHub</div>
					</div>
					<span className={css(styles.githubPanelStatus, statusStyle)}>
						<span className={css(styles.githubPanelStatusDot)} />
						{statusLabel}
					</span>
				</div>
				<div className={css(styles.githubPanelEmpty)}>GitHub integration is not available in this environment.</div>
			</section>
		);
	}

	return (
		<section className={css(styles.githubPanel)} aria-label='GitHub issue integration'>
			<div className={css(styles.githubPanelHeader)}>
				<span className={css(styles.githubPanelIcon)}>
					<GitHubGlyph size={22} />
				</span>
				<div className={css(styles.githubPanelHeading)}>
					<div className={css(styles.githubPanelTitle)}>GitHub</div>
				</div>
				<span className={css(styles.githubPanelStatus, statusStyle)}>
					<span className={css(styles.githubPanelStatusDot)} />
					{statusLabel}
				</span>
			</div>

			<div className={css(styles.githubPanelActions)}>
				{onCreateIssue && (
					<Button variant='primary' size='sm' onClick={onCreateIssue}>
						Create Issue
					</Button>
				)}
				{onLinkIssue && (
					<Button variant='outline' size='sm' onClick={onLinkIssue}>
						Link Existing
					</Button>
				)}
			</div>

			{query.isError && (
				<div className={css(styles.githubPanelError)}>
					{getApiErrorMessage(query.error, 'Failed to load GitHub panel data.')}
				</div>
			)}

			{query.isLoading && (
				<div className={css(styles.githubPanelLoading)}>
					<LoadingSpinner type='small' />
					<span>Loading GitHub data...</span>
				</div>
			)}

			{!query.isLoading && !query.isError && !hasAnyLinks && (
				<div className={css(styles.githubPanelEmpty)}>No GitHub issues linked yet.</div>
			)}

			{issues.length > 0 && (
				<div className={css(styles.githubIssueList)}>
					{issues.map(issue => (
						<GitHubIssueCard key={issue.id} ticketId={ticketId} issue={issue} />
					))}
				</div>
			)}

			{legacyIssues.length > 0 && (
				<div className={css(styles.githubLegacyList)}>
					<div className={css(styles.githubPanelSectionTitle)}>Manual links</div>
					{legacyIssues.map(issue => (
						<a
							key={issue.url}
							href={issue.url}
							target='_blank'
							rel='noopener noreferrer'
							className={css(styles.githubLegacyLink)}
						>
							{issue.repo}#{issue.number}
						</a>
					))}
				</div>
			)}
		</section>
	);
};

const GitHubIssueCard: React.FC<{ ticketId: string; issue: ISupportGitHubIssueLink }> = ({ ticketId, issue }) => {
	const [modal, setModal] = React.useState<'update' | null>(null);
	const [showUnlinkConfirm, setShowUnlinkConfirm] = React.useState(false);
	const syncMutation = useSyncGitHubIssueMutation(ticketId, issue.id);
	const unlinkMutation = useUnlinkGitHubIssueLinkMutation(ticketId, issue.id);
	const repoLabel = issue.repository || `${issue.owner}/${issue.repo}`;
	const stateStyle = issue.state === 'Closed' ? styles.githubIssueStateClosed : styles.githubIssueStateOpen;

	return (
		<div className={css(styles.githubIssueCard)}>
			<div className={css(styles.githubIssueCardMain)}>
				<div className={css(styles.githubIssueTitleRow)}>
					<a href={issue.url} target='_blank' rel='noopener noreferrer' className={css(styles.githubIssueTitle)}>
						{repoLabel}#{issue.number}
					</a>
					<span className={css(styles.githubIssueState, stateStyle)}>{issue.state}</span>
					<span className={css(styles.githubIssuePriority)}>{issue.priority}</span>
				</div>
				<div className={css(styles.githubIssueSummary)}>{issue.title || 'Untitled issue'}</div>
				<div className={css(styles.githubIssueLabels)}>
					{issue.status && <span className={css(styles.githubIssueLabel)}>{issue.status}</span>}
					{issue.labels?.map(label => (
						<span key={label} className={css(styles.githubIssueLabel)}>
							{label}
						</span>
					))}
					{issue.targetCodeRepos?.map(repo => (
						<span key={repo} className={css(styles.githubIssueTargetLabel)}>
							{repo}
						</span>
					))}
				</div>
				<div className={css(styles.githubPanelMeta)}>
					Last synced {issue.lastSyncedAt ? formatDateTime(issue.lastSyncedAt) : 'Never'}
				</div>
			</div>
			<div className={css(styles.githubIssueActions)}>
				<Button variant='outline' size='sm' onClick={() => setModal('update')}>
					Update
				</Button>
				<Button variant='outline' size='sm' onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
					{syncMutation.isPending ? 'Refreshing...' : 'Refresh'}
				</Button>
				<Button
					variant='outline'
					size='sm'
					onClick={() => setShowUnlinkConfirm(true)}
					disabled={unlinkMutation.isPending}
				>
					{unlinkMutation.isPending ? 'Unlinking...' : 'Unlink'}
				</Button>
			</div>
			{syncMutation.isError && (
				<div className={css(styles.githubPanelError)}>
					{getApiErrorMessage(syncMutation.error, 'Failed to refresh GitHub issue.')}
				</div>
			)}
			{unlinkMutation.isError && (
				<div className={css(styles.githubPanelError)}>
					{getApiErrorMessage(unlinkMutation.error, 'Failed to unlink GitHub issue.')}
				</div>
			)}
			{modal === 'update' && (
				<GitHubUpdateIssueModal ticketId={ticketId} issue={issue} onClose={() => setModal(null)} />
			)}
			{showUnlinkConfirm && (
				<ConfirmModal
					title='Unlink GitHub Issue'
					message={`Unlink ${repoLabel}#${issue.number} from this ticket?`}
					confirmLabel='Unlink'
					onConfirm={() => {
						unlinkMutation.mutate(undefined, { onSuccess: () => setShowUnlinkConfirm(false) });
					}}
					onCancel={() => setShowUnlinkConfirm(false)}
				/>
			)}
		</div>
	);
};
